import { Button, Flex, Image, Input, message, Space, Switch } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import { useNavigate, useSearchParams } from 'react-router-dom';
import './index.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArchiveSuggestionParams,
  Suggestion,
  SuggestionStatus,
} from '@/types/suggestion';
import { SUGGESTION_INDEX_KEY, SUGGESTION_IDS_KEY } from '.';
import { archiveSuggestion, getSuggestionDetail } from '@/api/suggestion';
import { useQuery } from '@tanstack/react-query';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { useAtomValue } from 'jotai';
import UploadImage from '@/components/UploadImage';

interface CheckItemProps {
  suggestion?: Suggestion;
  refresh: () => void;
}

const CheckItem = ({ suggestion, refresh }: CheckItemProps) => {
  const navigate = useNavigate();
  const isMobile = useAtomValue(isMobileAtom);
  const [messageApi, contextHolder] = message.useMessage();

  const [conclusion, setConclusion] = useState('');
  const [productImproveMethod, setProductImproveMethod] = useState('');
  const [toUserMessage, setToUserMessage] = useState('');
  const [toUserMessageSwitch, setToUserMessageSwitch] = useState(false);
  const [cover, setCover] = useState('');
  const [ratio, setRatio] = useState<number | undefined>(undefined);

  useEffect(() => {
    setConclusion(suggestion?.conclusion || '');
    setProductImproveMethod(suggestion?.productImproveMethod || '');
    setToUserMessage(suggestion?.userMsgContent || '');
    setCover(suggestion?.cover || '');
    setRatio(suggestion?.ratio);
  }, [suggestion]);

  // 上传后根据图片自然宽高自动计算 ratio = width / height，避免客户端 scaleAspectFill 截图
  const handleCoverChange = (url: string) => {
    setCover(url);
    if (!url) {
      setRatio(undefined);
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      if (img.width > 0 && img.height > 0) {
        setRatio(Number((img.width / img.height).toFixed(3)));
      }
    };
    img.src = url;
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 'bold',
    padding: isMobile ? 0 : '0 10px',
    width: 300,
    flexShrink: 0,
  };

  const renderImage = (imageUrls: string) => {
    const images = imageUrls.split(',');
    return (
      <Image.PreviewGroup>
        {images.map((image) => (
          <Image src={image} width={isMobile ? 100 : 150} key={image} />
        ))}
      </Image.PreviewGroup>
    );
  };

  const handleArchive = async () => {
    if (toUserMessageSwitch && !toUserMessage) {
      messageApi.info('请输入回复内容');
      return;
    }
    // 对齐 DB 列 user_msg_content varchar(1600) 与后端校验
    if (toUserMessageSwitch && toUserMessage.length > 1600) {
      messageApi.error(`回复内容长度不能超过 1600 字符 (当前 ${toUserMessage.length})`);
      return;
    }
    const params: ArchiveSuggestionParams = {
      id: suggestion?.id || '',
      conclusion,
      productImproveMethod,
      userMsgFlag: toUserMessageSwitch ? 1 : 0,
      userMsgContent: toUserMessage,
      cover: toUserMessageSwitch ? cover || undefined : undefined,
      ratio: toUserMessageSwitch ? ratio : undefined,
    };
    const resp = await archiveSuggestion(params);
    if (resp.status !== 200) {
      messageApi.error(resp.message);
      return;
    }
    messageApi.success('Archive successfully');
    refresh();
  };

  const handleCancel = () => {
    sessionStorage.removeItem(SUGGESTION_INDEX_KEY);
    sessionStorage.removeItem(SUGGESTION_IDS_KEY);
    navigate('/suggestion');
  };

  const renderName = () => {
    if (!suggestion) {
      return '-';
    }
    return suggestion.name || suggestion.nickName;
  };

  return (
    <div>
      {contextHolder}
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Name</div>
        <div>{renderName()}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Supp ID</div>
        <div>{suggestion?.userNumber || '-'}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Suggestion ID</div>
        <div>{suggestion?.id || '-'}</div>
      </Flex>
      <Flex
        style={{ marginBottom: 10, flexDirection: isMobile ? 'column' : 'row' }}
      >
        <div style={labelStyle}>Latest Check</div>
        <div>{suggestion?.viewUserId || '-'}</div>
      </Flex>
      <Flex
        style={{ marginBottom: 10, flexDirection: isMobile ? 'column' : 'row' }}
      >
        <div style={labelStyle}>Feedback Time</div>
        <div>
          {suggestion?.createTime
            ? dayjs.utc(suggestion.createTime).add(9, 'hour').format('YYYY-MM-DD HH:mm:ss') + ' (JST)'
            : '-'}
        </div>
      </Flex>
      <Flex
        style={{ marginBottom: 10, flexDirection: isMobile ? 'column' : 'row' }}
      >
        <div style={labelStyle}>Suggested Reason</div>
        <div>{suggestion?.feedbackName || '-'}</div>
      </Flex>
      <Flex
        style={{ marginBottom: 10, flexDirection: isMobile ? 'column' : 'row' }}
      >
        <div style={labelStyle}>Suggestion Description</div>
        <div style={{ maxWidth: 500, wordWrap: 'break-word' }}>
          {suggestion?.userRemark || '-'}
        </div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>设备类型</div>
        <div>
          {suggestion?.deviceType
            ? {
                1: 'IOS APP',
                2: 'Android APP',
                3: 'iOS H5',
                4: 'Android H5',
                5: 'Other/PC',
              }[suggestion.deviceType] || '-'
            : '-'}
        </div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>SUPPID</div>
        <div>{suggestion?.suppId || '-'}</div>
      </Flex>
      <div style={{ marginBottom: 10 }}>
        <div style={labelStyle}>Suggestion Picture</div>
        <Flex style={{ marginTop: 10, overflowX: 'auto' }}>
          {renderImage(suggestion?.userImageUrls || '')}
        </Flex>
      </div>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Review Remark</div>
        <Input.TextArea
          value={conclusion}
          onChange={(e) => setConclusion(e.target.value)}
          disabled={suggestion?.status === SuggestionStatus.ARCHIVED}
        />
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Product improvement Method</div>
        <Input.TextArea
          value={productImproveMethod}
          onChange={(e) => setProductImproveMethod(e.target.value)}
          disabled={suggestion?.status === SuggestionStatus.ARCHIVED}
          rows={4}
        />
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>
          <span style={{ color: 'red', marginRight: 4 }}>*</span>
          To User Message
        </div>
        {/* flex:1 + minWidth:0 让内层在 row 父级中正确收缩, 避免长字符串溢出撑爆布局 */}
        <Flex style={{ flexDirection: 'column', flex: 1, minWidth: 0 }} gap={10}>
          <Switch
            style={{ width: '30px' }}
            checked={toUserMessageSwitch}
            onChange={(checked) => setToUserMessageSwitch(checked)}
            disabled={suggestion?.status === SuggestionStatus.ARCHIVED}
          />
          <Input.TextArea
            value={toUserMessage}
            onChange={(e) => setToUserMessage(e.target.value)}
            disabled={!toUserMessageSwitch}
            rows={4}
            maxLength={1600}
            showCount
          />
          <div
            style={{
              opacity:
                !toUserMessageSwitch ||
                suggestion?.status === SuggestionStatus.ARCHIVED
                  ? 0.5
                  : 1,
              pointerEvents:
                !toUserMessageSwitch ||
                suggestion?.status === SuggestionStatus.ARCHIVED
                  ? 'none'
                  : 'auto',
            }}
          >
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
              通知封面图（选填，建议 3:4 竖图 600×800）
            </div>
            <UploadImage
              value={cover}
              onChange={handleCoverChange}
              width={300}
              height={400}
              maxSize={5}
              showSizeHint
              showResolution
              acceptWebp
            />
            {ratio !== undefined && (
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                ratio = {ratio}
              </div>
            )}
          </div>
        </Flex>
      </Flex>
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Space size="small">
          <Button
            type="primary"
            onClick={handleArchive}
            disabled={suggestion?.status === SuggestionStatus.ARCHIVED}
          >
            ARCHIVE
          </Button>
          <Button onClick={handleCancel}>CANCEL</Button>
        </Space>
      </div>
    </div>
  );
};

const CheckPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentIndex, setCurrentIndex] = useState(
    parseInt(searchParams.get(SUGGESTION_INDEX_KEY) || '0')
  );
  const [currentId, setCurrentId] = useState('');
  const idsRef = useRef<string[]>([]);

  const { data: suggestionDetail, refetch } = useQuery({
    queryKey: ['suggestionDetail', currentId],
    queryFn: async () => {
      const resp = await getSuggestionDetail(currentId);
      return resp.data;
    },
    enabled: !!currentId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const ids = searchParams.get(SUGGESTION_IDS_KEY)?.split(',') || [];
    if (ids.length && !idsRef.current.length) {
      idsRef.current = ids;
      setCurrentId(ids[currentIndex]);
    }
  }, [currentIndex, searchParams]);

  const handlePrevClick = useCallback(() => {
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    setCurrentId(idsRef.current[newIndex]);
    const params = new URLSearchParams(searchParams);
    params.set(SUGGESTION_INDEX_KEY, newIndex.toString());
    setSearchParams(params);
  }, [currentIndex, searchParams, setSearchParams]);

  const handleNextClick = useCallback(() => {
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    setCurrentId(idsRef.current[newIndex]);
    const params = new URLSearchParams(searchParams);
    params.set(SUGGESTION_INDEX_KEY, newIndex.toString());
    setSearchParams(params);
  }, [currentIndex, searchParams, setSearchParams]);

  return (
    <div className="check-page-container">
      <div className="pagination-btn-container">
        <Button
          type="primary"
          className="pagination-btn-left"
          onClick={handlePrevClick}
          disabled={currentIndex === 0}
          shape="circle"
        >
          &lt;
        </Button>
      </div>

      <div className="check-item-container">
        <CheckItem suggestion={suggestionDetail} refresh={refetch} />
      </div>

      <div className="pagination-btn-container">
        <Button
          type="primary"
          className="pagination-btn-right"
          onClick={handleNextClick}
          disabled={currentIndex === idsRef.current.length - 1}
          shape="circle"
        >
          &gt;
        </Button>
      </div>
    </div>
  );
};

export default CheckPage;
